// The restaurant's clock.
//
// Opening hours are wall-clock times where the restaurant is ("11:00–21:00"),
// and every order time is shown on that clock. restaurant.timezone (an IANA
// name like "America/Chicago") says which clock that is; the backend always
// sends one.
//
// Calendar days are passed around as local-midnight Dates whose year, month and
// date are the RESTAURANT's calendar day — so day.getDay(), moment(day) and the
// existing day pickers keep working unchanged. Slots and other instants are real
// instants.
//
// With no zone (an older API, a page that doesn't know the restaurant) every
// function falls back to this browser's clock, which is what the site did before.

const validZones = new Map();
export function isValidZone(tz) {
  if (!tz || typeof tz !== "string") return false;
  if (!validZones.has(tz)) {
    try {
      new Intl.DateTimeFormat("en-US", { timeZone: tz });
      validZones.set(tz, true);
    } catch (e) {
      validZones.set(tz, false);
    }
  }
  return validZones.get(tz);
}

const zoneOf = (tz) => (isValidZone(tz) ? tz : null);

const partsFormatters = new Map();
function partsFormatter(tz) {
  if (!partsFormatters.has(tz)) {
    partsFormatters.set(
      tz,
      new Intl.DateTimeFormat("en-US", {
        timeZone: tz,
        hourCycle: "h23",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    );
  }
  return partsFormatters.get(tz);
}

const pad = (n) => String(n).padStart(2, "0");

/**
 * The wall clock at instant `at` on the restaurant's clock:
 * { y, m (1-12), d, dayIdx (0 = Sunday), minutes, seconds, dateStr 'YYYY-MM-DD' }.
 */
export function wallParts(at = new Date(), tz) {
  const date = at instanceof Date ? at : new Date(at);
  const zone = zoneOf(tz);
  let y, m, d, hh, mm, ss;
  if (zone) {
    const p = {};
    for (const part of partsFormatter(zone).formatToParts(date)) p[part.type] = part.value;
    y = Number(p.year);
    m = Number(p.month);
    d = Number(p.day);
    hh = Number(p.hour) % 24;
    mm = Number(p.minute);
    ss = Number(p.second);
  } else {
    y = date.getFullYear();
    m = date.getMonth() + 1;
    d = date.getDate();
    hh = date.getHours();
    mm = date.getMinutes();
    ss = date.getSeconds();
  }
  return {
    y,
    m,
    d,
    dayIdx: new Date(Date.UTC(y, m - 1, d)).getUTCDay(),
    minutes: hh * 60 + mm,
    seconds: ss,
    dateStr: `${y}-${pad(m)}-${pad(d)}`,
  };
}

// Minutes `tz` is ahead of UTC at `date`.
function offsetMinutes(tz, date) {
  const w = wallParts(date, tz);
  const asUtc = Date.UTC(w.y, w.m - 1, w.d, Math.floor(w.minutes / 60), w.minutes % 60, w.seconds);
  return Math.round((asUtc - Math.floor(date.getTime() / 1000) * 1000) / 60000);
}

/**
 * The instant the restaurant's clock reads `minutes` past midnight on `dateStr`
 * ('YYYY-MM-DD'). Minutes past 1439 roll into the next day. Right on both sides
 * of a daylight-saving change.
 */
export function zonedWallTimeToUtc(dateStr, minutes, tz) {
  const [y, m, d] = String(dateStr).split("-").map(Number);
  const mins = Number(minutes) || 0;
  const zone = zoneOf(tz);
  if (!zone) return new Date(y, m - 1, d, 0, mins, 0, 0);
  const wallAsUtc = Date.UTC(y, m - 1, d, 0, mins);
  let guess = wallAsUtc;
  for (let i = 0; i < 3; i++) {
    const next = wallAsUtc - offsetMinutes(zone, new Date(guess)) * 60000;
    if (next === guess) break;
    guess = next;
  }
  return new Date(guess);
}

/** 'YYYY-MM-DD' of a local-midnight day Date (its own local fields). */
export const dayKeyOf = (day) => `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;

/** The restaurant's calendar day of instant `at`, as a local-midnight Date. */
export function restaurantDayOf(at = new Date(), tz) {
  const w = wallParts(at, tz);
  return new Date(w.y, w.m - 1, w.d);
}

/** Today on the restaurant's clock, as a local-midnight Date. */
export const restaurantToday = (tz, now = new Date()) => restaurantDayOf(now, tz);

// ── Showing times ────────────────────────────────────────────────────────────

const abbrevFormatters = new Map();
function zoneAbbrev(at, tz) {
  const key = tz || "";
  if (!abbrevFormatters.has(key)) {
    abbrevFormatters.set(
      key,
      new Intl.DateTimeFormat("en-US", { hour: "numeric", timeZoneName: "short", ...(tz ? { timeZone: tz } : {}) })
    );
  }
  const part = abbrevFormatters.get(key).formatToParts(at).find((p) => p.type === "timeZoneName");
  return part ? part.value : "";
}

/**
 * " CST" when the restaurant's clock reads differently from this browser's at
 * `at`, '' when they agree — so a Boston customer of a Boston restaurant sees
 * "2:00 PM", and one in Chicago sees "2:00 PM EST".
 */
export function zoneSuffix(at, tz) {
  const zone = zoneOf(tz);
  if (!zone) return "";
  const date = at instanceof Date ? at : new Date(at);
  if (Number.isNaN(date.getTime())) return "";
  const theirs = zoneAbbrev(date, zone);
  return theirs && theirs !== zoneAbbrev(date, null) ? ` ${theirs}` : "";
}

const toDate = (v) => {
  if (v == null || v === "") return null;
  const d = v instanceof Date ? v : new Date(/^\d+$/.test(String(v)) ? Number(v) : v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Format on the restaurant's clock with Intl options (no zone suffix). */
export function formatIn(v, tz, opts) {
  const d = toDate(v);
  if (!d) return "";
  const zone = zoneOf(tz);
  return new Intl.DateTimeFormat("en-US", { ...opts, ...(zone ? { timeZone: zone } : {}) }).format(d);
}

/** "2:00 PM", or "2:00 PM CST" when that isn't this browser's clock. */
export function timeIn(v, tz) {
  const d = toDate(v);
  if (!d) return "";
  return formatIn(d, tz, { hour: "numeric", minute: "2-digit" }) + zoneSuffix(d, tz);
}

/** "Tue, Nov 3 at 2:00 PM" (+ zone when it differs). */
export function dayTimeIn(v, tz) {
  const d = toDate(v);
  if (!d) return "";
  return `${formatIn(d, tz, { weekday: "short", month: "short", day: "numeric" })} at ${timeIn(d, tz)}`;
}
